<template>
  <div class="group-creator">
    <div class="creator-header">
      <h3>👥 グループ作成</h3>
      <p class="creator-subtitle">新しい講師グループを作成します</p>
    </div>
    
    <div class="creator-form">
      <div class="form-group">
        <label for="groupName">グループ名</label>
        <input 
          id="groupName"
          v-model="groupName" 
          type="text" 
          placeholder="例: Group A" 
          class="form-input"
          :disabled="isCreating"
        />
      </div>
      
      <div class="form-group">
        <label for="groupDescription">説明（任意）</label>
        <textarea 
          id="groupDescription"
          v-model="groupDescription" 
          placeholder="グループの説明を入力してください"
          class="form-textarea"
          :disabled="isCreating"
          rows="3"
        ></textarea>
      </div>
      
      <div class="form-actions">
        <button 
          class="btn btn-create"
          :disabled="!groupName.trim() || isCreating"
          @click="createGroup"
        >
          {{ isCreating ? '作成中...' : '✅ グループ作成' }}
        </button>
        
        <button 
          class="btn btn-cancel"
          :disabled="isCreating"
          @click="resetForm"
        >
          キャンセル
        </button>
      </div>
    </div>
    
    <!-- 成功メッセージ -->
    <div v-if="showSuccess" class="success-message">
      <div class="success-icon">✅</div>
      <div class="success-text">
        <strong>グループ「{{ lastCreatedGroup }}」を作成しました！</strong>
        <p>講師の割当で使用できるようになりました。</p>
      </div>
    </div>
    
    <!-- エラーメッセージ -->
    <div v-if="errorMessage" class="error-message">
      <div class="error-icon">❌</div>
      <div class="error-text">
        <strong>エラーが発生しました</strong>
        <p>{{ errorMessage }}</p>
      </div>
      <button class="btn btn-dismiss" @click="errorMessage = ''">閉じる</button>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { supabase } from '../../supabaseClient'

// イベント定義
const emit = defineEmits(['created'])

// 状態管理
const groupName = ref('')
const groupDescription = ref('')
const isCreating = ref(false)
const showSuccess = ref(false)
const errorMessage = ref('')
const lastCreatedGroup = ref('')

// グループ作成
const createGroup = async () => {
  if (!groupName.value.trim()) {
    errorMessage.value = 'グループ名を入力してください'
    return
  }

  isCreating.value = true
  errorMessage.value = ''
  
  try {
    const payload = {
      name: groupName.value.trim(),
      description: groupDescription.value.trim() || null
    }

    const { data, error } = await supabase
      .from('instructor_groups')
      .insert(payload)
      .select()

    if (error) {
      // 重複エラーの場合
      if (error.code === '23505') {
        throw new Error('同じ名前のグループが既に存在します')
      }
      throw error
    }

    // 成功処理
    lastCreatedGroup.value = groupName.value.trim()
    showSuccess.value = true
    resetForm()
    
    // 親コンポーネントに通知
    emit('created')
    
    // 3秒後に成功メッセージを非表示
    setTimeout(() => {
      showSuccess.value = false
    }, 3000)

  } catch (error) {
    console.error('グループ作成エラー:', error)
    errorMessage.value = error.message || 'グループの作成に失敗しました'
  } finally {
    isCreating.value = false
  }
}

// グループメンバー数のチェック（将来の拡張用）
const checkGroupSize = async (groupId) => {
  try {
    const { data, error } = await supabase
      .from('instructor_group_members')
      .select('instructor_id', { count: 'exact' })
      .eq('group_id', groupId)

    if (error) throw error

    const memberCount = data?.length || 0
    if (memberCount >= 5) {
      console.warn(`グループ ${groupId} は既に5人です（現在: ${memberCount}人）`)
      return false
    }

    return true
  } catch (error) {
    console.error('グループサイズチェックエラー:', error)
    return true // エラー時は許可
  }
}

// フォームリセット
const resetForm = () => {
  groupName.value = ''
  groupDescription.value = ''
  errorMessage.value = ''
}
</script>

<style scoped>
.group-creator {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.creator-header {
  margin-bottom: 1.5rem;
  text-align: center;
}

.creator-header h3 {
  color: #1f2937;
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
}

.creator-subtitle {
  color: #6b7280;
  font-size: 0.9rem;
}

.creator-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
}

.form-input,
.form-textarea {
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:disabled,
.form-textarea:disabled {
  background: #f9fafb;
  color: #6b7280;
  cursor: not-allowed;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 0.5rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-create {
  background: #10b981;
  color: white;
}

.btn-create:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.btn-cancel {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-cancel:hover:not(:disabled) {
  background: #e5e7eb;
}

.btn-dismiss {
  background: #ef4444;
  color: white;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  min-width: auto;
}

.btn-dismiss:hover {
  background: #dc2626;
}

/* メッセージ表示 */
.success-message,
.error-message {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 6px;
  margin-top: 1rem;
  animation: slideIn 0.3s ease-out;
}

.success-message {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.success-icon,
.error-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.success-text,
.error-text {
  flex: 1;
}

.success-text strong,
.error-text strong {
  display: block;
  margin-bottom: 0.25rem;
}

.success-text p,
.error-text p {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.8;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* レスポンシブ対応 */
@media (max-width: 768px) {
  .group-creator {
    padding: 1rem;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
}
</style>
